import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    getCart,
    addCartItem as addCartItemApi,
    updateCartItem as updateCartItemApi
} from "@/api/customer.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { ROLES } from "@/utils/constants.js";

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const { isAuthenticated, role } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const refreshCart = useCallback(async () => {
        if (!isAuthenticated || role !== ROLES.CUSTOMER) {
            setItems([]);
            return;
        }
        setLoading(true);
        try {
            const data = await getCart();
            setItems(data.data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, role]);

    useEffect(() => {
        refreshCart();
    }, [refreshCart]);

    const addToCart = useCallback(async (productId, quantity = 1) => {
        const data = await addCartItemApi(productId, quantity);
        setItems(data.data || []);
        return data;
    }, []);

    /** Set absolute cart quantity for a product (add if missing, update if present). */
    const setCartQuantity = useCallback(async (productId, quantity) => {
        const existing = items.some((item) => String(item.productId) === String(productId));
        const data = existing
            ? await updateCartItemApi(productId, quantity)
            : await addCartItemApi(productId, quantity);
        setItems(data.data || []);
        return data;
    }, [items]);

    const itemCount = useMemo(
        () => items.reduce((sum, item) => sum + (item.quantity || 0), 0),
        [items]
    );

    const value = useMemo(
        () => ({ items, itemCount, loading, refreshCart, addToCart, setCartQuantity, setItems }),
        [items, itemCount, loading, refreshCart, addToCart, setCartQuantity]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within CartProvider");
    }
    return context;
}
